// utils\firebaseFunctions_original.ts

import {
  doc,
  setDoc,
  serverTimestamp,
  query,
  orderBy,
  updateDoc,
  getDoc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { collection, where, getDocs } from "firebase/firestore";
import { db, storage } from "../firebase";
import {
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { fetchData, fetchSingleRow } from "@/supabase/functions";
import { supabase } from "@/supabase/client";

export const checkUserExistenceOnly = async (address: string) => {
  try {
    if (!address) {
      return;
    }
    const lowercasedAddress = address.toLowerCase();
    
    const userReq = await fetchSingleRow("users", "wallet_lc", lowercasedAddress);
    return userReq;
  } catch (error) {
    console.error("Error checking user existence:", error);
  }
};

export const checkUserExistencebyId = async (user_id: string) => {
  try {
    if (!user_id) {
      return;
    }
    const userReq = await fetchSingleRow("users", "uid", user_id);
    return userReq;
  } catch (error) {
    console.error("Error checking user existence:", error);
  }
};

export const getUserWithUID = async (uid: string) => {
  try {
    if (!uid) {
      return;
    }
    const userReq = await fetchSingleRow("users", "uid", uid);
    return userReq;
  } catch (error) {
    console.error("Error checking user existence:", error);
    throw error;
  }
};

export const checkIfUserHasLoginAuditToday = async (uid: any) => {
  const today = new Date();
  const startOfDay = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  const endOfDay = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate() + 1
    )
  );
  const startOfDayTime = startOfDay.getTime() / 1000;
  const endOfDayTime = endOfDay.getTime() / 1000;

  try {
    const result = await fetchData(
      'sys_audit',
      {
        select: '*',
        filter: {
          uid: uid,
          event: 'LOGIN',
          sub_event: 'WEBSITE' 
        }
      },
      {
        created_at: { 
          gt: startOfDayTime,
          lt: endOfDayTime
        } 
      }
    );

    return result.data.length > 0;
  } catch (error) {
    console.error("Error checking if user has login audit today:", error);
    return false;
  }
};

export const fetchUserReferralsByUID = async (uid: string) => {
  try {
    const url = `https://us-central1-prodmxs.cloudfunctions.net/getUserReferrals?uid=${uid}`;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "aad235a0-7fcc-079f-e33f-53c65fbc4c81",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching user referrals by UID:", error);
    return null;
  }
};

export const getSignupReferralRewardsByUID = async (uid: string) => {
  try {
    const rewards = await fetchData("sys_rewards", {
      select: "*",
      filter: {
        uid: uid,
        type: "REFERRAL",
        sub_event: "REFERRAL_SIGNUP",
        event: "REWARD"
      }
    });

    if (rewards.data.length === 0) {
      return null;
    } else {
      return rewards.data;
    }
  } catch (error) {
    console.error("Error fetching signup referral rewards by UID:", error);
    throw error;
  }
};

export const getUserReferralsLeaderboardRecords = async (uid: string, tabIndex: number) => {
  try {
    const type = tabIndex === 0 ? "signup" : "active";

    const url = `https://us-central1-prodmxs.cloudfunctions.net/getUserReferralsLeaderboardRank?uid=${uid}&type=${type}`;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "aad235a0-7fcc-079f-e33f-53c65fbc4c81",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data || !data.data) {
      return null;
    }

    return data.data;
  } catch (error) {
    console.error("Error fetching user referrals leaderboard records:", error);
    return null;
  }
};

export const getUserReferralsLeaderboardStats = async (uid: string) => {
  try {
    const url = `https://us-central1-prodmxs.cloudfunctions.net/getUserReferralsStats?uid=${uid}`;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "aad235a0-7fcc-079f-e33f-53c65fbc4c81",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data || !data.data || data.data.length === 0) {
      return null;
    }

    return data.data[0];
  } catch (error) {
    console.error("Error fetching user referrals leaderboard records:", error);
    return null;
  }
};

export const getRewardsByUID = async (uid: string) => {
  try {
    const rewards = await fetchData("sys_rewards", {
      select: "*",
      filter: {
        uid: uid
      },
      order: {
        created_at: "desc",
      }
    });

    return rewards.data;
  } catch (error) {
    console.error("Error fetching collection count:", error);
    return 0;
  }
};

export const checkRewardClaimed = async (uid: string, missionId: string) => {
  try {
    const rewards = await fetchData("sys_rewards", {
      select: "*",
      filter: {
        uid: uid,
        mission_id: missionId,
      }
    });
    return rewards.data.length > 0;
  } catch (error) {
    console.error("Error fetching collection count:", error);
    return false;
  }
};

// ***************************************************************** //
// ***************************************************************** //
// ***************************************************************** //


export const getNodeReferredCount = async (
  referralFromUid: any
): //   collectionName: string
Promise<number> => {
  try {
    const querySnapshot = await getDocs(
      query(
        collection(db, "referral_payments"),
        where("referral_from_uid", "==", referralFromUid)
      )
    );
    return querySnapshot.size;
  } catch (error) {
    console.error("Error fetching collection count:", error);
    return 0;
  }
};

export const getMyNodesPurchasedCount = async (
  referralFromUid: any
): //   collectionName: string
Promise<number> => {
  try {
    const querySnapshot = await getDocs(
      query(
        collection(db, "node_purchased"),
        where("uid", "==", referralFromUid)
      )
    );
    return querySnapshot.size;
  } catch (error) {
    console.error("Error fetching collection count:", error);
    return 0;
  }
};

export const getNodeCountByOwnerNFT = async (
  contractAddress: string,
  ownerWallet: string
) => {
  try {
    // Reference to the 'tokens' collection for the specific contract
    const tokensCollectionRef = collection(
      db,
      "nfts",
      "ecosystem",
      "contracts",
      contractAddress,
      "tokens"
    );

    console.log({ ownerWallet });

    // Query to fetch nodes where the current owner matches the provided wallet
    const nodesQuery = query(
      tokensCollectionRef,
      where("current_owner", "==", ownerWallet.toLowerCase())
    );

    // Fetch all the documents that match the query
    const nodesSnapshot = await getDocs(nodesQuery);

    console.log(nodesSnapshot);

    // The number of documents corresponds to the number of nodes owned by the wallet
    const nodeCount = nodesSnapshot.size;

    console.log(`Total number of nodes owned by ${ownerWallet}: ${nodeCount}`);
    return nodeCount;
  } catch (error) {
    console.error("Error getting node count: ", error);
    throw new Error("Failed to get node count");
  }
};

export const getMyFrNodesPurchasedCount = async (
  referralFromUid: any
): //   collectionName: string
Promise<number> => {
  try {
    const querySnapshot = await getDocs(
      query(
        collection(db, "node_purchased_fractional"),
        where("uid", "==", referralFromUid)
      )
    );
    console.log("Size ", querySnapshot.size);
    return querySnapshot.size;
  } catch (error) {
    console.error("Error fetching collection count:", error);
    return 0;
  }
};

export async function updateUserField(
  walletAddress: string,
  field: string,
  newValue: string
) {
  try {
    // Query for the user document with the matching wallet address
    const q = query(
      collection(db, "users"),
      where("wallet", "==", walletAddress)
    );
    const querySnapshot = await getDocs(q);

    // Check if a user document was found
    if (!querySnapshot.empty) {
      // Get the first document from the query results
      const userDoc = querySnapshot.docs[0];

      // Update the field of the user document
      await updateDoc(userDoc.ref, {
        [field]: newValue,
      });

      console.log(
        `User with wallet address ${walletAddress}'s ${field} has been updated to ${newValue}`
      );
    } else {
      console.log(`No user found with wallet address ${walletAddress}`);
    }
  } catch (error) {
    console.error(`Error updating user field: ${error}`);
    return error;
  }
};

export const getUserHandles = async (uid: string) => {
  try{
    const userHandles = [];

    const { data, error } = await fetchData("users_handles", {
      select: "*",
      filter: {
        uid: uid
      }
    });

    for (const handle of data) {
      userHandles.push(handle);
    }

    return userHandles;
  } catch (error) {
    console.error("Error fetching user handles:", error);
    throw error;
  }
};

export const unVerifyHandle = async (uid: string, handle: string) => {
  try {
    const { error } = await supabase
      .from("users_handles")
      .update({
        handle_verified: false,
        handle_verified_at: null,
      })
      .eq("uid", uid)
      .eq("handle_name", handle);

    if (error) {
      throw error;
    }

    if(handle === 'email'){
      const { error } = await supabase
      .from("users")
      .update({
        email: null,
      })
      .eq("uid", uid);

      if (error) {
        throw error;
      }
    }

    return true;
  } catch (error) {
    console.error("Error unverifying handle:", error);
    throw error;
  }
};

export const updateUserHandle = async (
  uid: string,
  handle: string,
  handleData: any
) => {
  try {

    delete handleData.date_timestamp;
    delete handleData.firebase_timestamp;
    delete handleData.timestamp_dt;

    const { error } = await supabase
      .from("users_handles")
      .upsert([
        {
          uid: uid,
          ...handleData
        },
      ]).eq("handle_name", handle);

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error updating user handle:", error);
    throw error;
  }
};

export const uploadUserProfilePicture = async (
  uid: string,
  userData: any
) => {
  try {
    const storageRef = ref(storage, `users/${uid}`);

    try {
      await getDownloadURL(storageRef);

      await deleteObject(storageRef);
    } catch (error) {}

    await uploadBytes(storageRef, userData.profilePicture);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error("Error uploading profile picture:", error);
    throw error;
  }
};

export const uploadReportScreenshot = async (
  reportId: string,
  screenshot: any
) => {
  try {
    const storageRef = ref(storage, `report_screenshots/${reportId}`);
    await uploadBytes(storageRef, screenshot);
    return (await getDownloadURL(storageRef)) ?? null;
  } catch (error) {
    console.error("Error uploading report screenshot:", error);
    throw error;
  }
};

export const uploadReportScreenshots = async (
  reportId: string,
  screenshots: (string | File)[]
) => {
  try {
    const downloadURLs = [];

    console.log("Screenshots:", screenshots);

    for (let i = 0; i < screenshots.length; i++) {
      const screenshot = screenshots[i];

      if (typeof screenshot === "string") {
        // If the screenshot is a string, it's a URL of an existing screenshot
        downloadURLs.push(screenshot);
      } else {
        // If the screenshot is a file, upload it to the storage
        const filePath = `report_screenshots/${reportId}/${new Date().getTime()}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('screenshots')
          .upload(filePath, screenshot, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw uploadError;
        }

        const { data: publicURLData } = supabase.storage
          .from('screenshots')
          .getPublicUrl(filePath);

        downloadURLs.push(publicURLData.publicUrl);
      }
    }

    console.log("Download URLs:", downloadURLs);

    return downloadURLs;
  } catch (error) {
    console.error("Error uploading report screenshots:", error);
    throw error;
  }
};

export const addUserReport = async (reportData: any) => {
  try {
    // Convert JavaScript timestamps (milliseconds) to ISO strings for PostgreSQL
    const dateTimeIssue = reportData.date_time_issue ? new Date(reportData.date_time_issue).toISOString() : null;
    const datetimeCurrent = reportData.datetime_current ? new Date(reportData.datetime_current).toISOString() : null;
    
    const { error } = await supabase
      .from('user_reports')
      .insert({
        uid: reportData.uid,
        wallet: reportData.wallet,
        description: reportData.description,
        status: reportData.status || null,
        severity: reportData.severity || null,
        issue_area: reportData.issue_area,
        game_name: reportData.game_name || null,
        date_time_issue: dateTimeIssue,
        datetime_current: datetimeCurrent,
        admin_is_rewarded: reportData.admin_is_rewarded || false,
        admin_points_awarded: reportData.admin_points_awarded || 0,
        round: reportData.round || null,
        buy_from: reportData.buy_from || null,
        purchase_location: reportData.purchase_location || null,
        screenshots: reportData.screenshots || null,
        report_id: reportData.report_id,
        claiming_from: reportData.claiming_from || null,
        sub_category: reportData.sub_category || null,
        wallet_type: reportData.wallet_type || null,
        discord: reportData.discord || null,
        email: reportData.email || null,
        telegram: reportData.telegram || null
      });

    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("Error adding user report:", error);
    throw error;
  }
};

export const getUserWithReferralAlias = async (referralAlias: string) => {
  try {
    const userQuery = query(
      collection(db, "users"),
      where("referral_alias", "==", referralAlias)
    );
    const userSnapshot = await getDocs(userQuery);

    if (!userSnapshot.empty) {
      return userSnapshot.docs[0].data();
    }

    return null;
  } catch (error) {
    console.error("Error fetching user with referral alias:", error);
    throw error;
  }
};

export const updateUserFieldWithUID = async (
  uid: string,
  field: string,
  value: any
) => {
  try {
    await updateDoc(doc(db, "users", uid), {
      [field]: value,
    });

    console.log(`User field "${field}" updated successfully.`);

    return true;
  } catch (error) {
    console.error("Error updating user field with UID:", error);
    throw error;
  }
};

export const getMessages = async (uid = null) => {
  try {
    let messages = [];

    // Query for broadcast messages
    const { data: broadcastMessages, error: broadcastError } = await supabase
      .from('messages')
      .select('*')
      .eq('is_visible', true)
      .eq('message_type', 'broadcast');

    if (broadcastError) {
      throw broadcastError;
    }

    messages = broadcastMessages || [];

    if (uid !== null) {
      // If uid is not null, query for personal messages where target_uid matches uid
      const { data: personalMessages, error: personalError } = await supabase
        .from('messages')
        .select('*')
        .eq('is_visible', true)
        .eq('message_type', 'personal')
        .eq('target_uid', uid);

      if (personalError) {
        throw personalError;
      }

      // Merge the broadcast messages and personal messages
      messages = [...messages, ...(personalMessages || [])];

      // Get all message IDs
      const messageIds = messages.map(message => message.id);
      
      // Fetch all seen records for this user in a single query
      const { data: seenRecords, error: seenError } = await supabase
        .from('messages_seen')
        .select('*')
        .eq('user_id', uid)
        .in('message_id', messageIds);
        
      if (seenError) {
        throw seenError;
      }
      
      // Create a map of message_id to seen status for faster lookup
      const seenStatusMap: { [key: string]: boolean } = {};
      seenRecords?.forEach(record => {
        seenStatusMap[record.message_id] = record.seen;
      });
      
      // Set the is_seen field for each message
      for (const message of messages) {
        message.is_seen = seenStatusMap[message.id] || false;
      }
    }

    // Sort the messages by is_seen and created_at
    messages.sort((a, b) => {
      if (a.is_seen === b.is_seen) {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      } else {
        return a.is_seen ? 1 : -1;
      }
    });

    return messages;
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
};

export const getShowMissionsFlag = async () => {
  // it'll be here: /game_ecosystem/game/gamadata/admin_flags in field show_missions
  try {
    const docRef = doc(db, "game_ecosystem/game/gamadata/admin_flags");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().show_missions === "true";
    } else {
      console.log("No such document!");
    }
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
};

export const getShowLeaderboardIssueMessageFlag = async () => {
  // it'll be here: /game_ecosystem/game/gamadata/admin_flags in field show_missions
  try {
    const docRef = doc(db, "game_ecosystem/game/gamadata/admin_flags");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().show_leaderboard_issue_message === "true";
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
};

export const getUserReports = async (uid: string) => {
  try {
    const querySnapshot = await getDocs(
      query(
        collection(db, "user_reports"),
        where("uid", "==", uid),
        orderBy("updated_at", "desc"),
        orderBy("datetime_current", "desc")
      )
    );

    let data = querySnapshot.docs.map((doc) => {
      const report = { id: doc.id, ...doc.data() };
      return report;
    });

    data = await Promise.all(
      data.map(async (report) => {
        const commentsSnapshot = await getDocs(
          query(
            collection(db, "user_report_comments"),
            where("report_id", "==", report.id),
            orderBy("created_at", "asc")
          )
        );
        const comments = commentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        return { ...report, comments };
      })
    );

    return data;
  } catch (error) {
    console.error("Error fetching user reports:", error);
    throw error;
  }
};

export const removeUserReport = async (report_id: string) => {
  try {
    const docRef = doc(db, "user_reports", report_id);
    await deleteDoc(docRef);
    console.log("Report successfully deleted!");
    return true;
  } catch (error) {
    console.error("Error deleting report:", error);
    return false;
  }
};

export const updateUserReport = async (report_id: string, data: any) => {
  try {
    // Handle date fields if present in the data
    if (data.date_time_issue) {
      data.date_time_issue = new Date(data.date_time_issue).toISOString();
    }
    if (data.datetime_current) {
      data.datetime_current = new Date(data.datetime_current).toISOString();
    }
    
    const { error } = await supabase
      .from('user_reports')
      .update(data)
      .eq('report_id', report_id);

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error updating report:", error);
    throw error;
  }
};

export const updateUserReportComments = async (data: {
  uid: any;
  user_comment: any;
  created_at: any;
  report_id: any;
}) => {
  try {
    const userReportCommentsQuery = query(
      collection(db, "user_report_comments"),
      where("user_id", "==", data.uid),
      where("user_comment", "==", data.user_comment)
    );

    const userReportCommentsSnapshot = await getDocs(userReportCommentsQuery);

    if (userReportCommentsSnapshot.empty) {
      const newCommentRef = doc(collection(db, "user_report_comments"));
      await setDoc(newCommentRef, {
        user_id: data.uid,
        user_comment: data.user_comment,
        created_at: data.created_at,
        report_id: data.report_id,
      });

      const userReportsQuery = query(
        collection(db, "user_reports"),
        where("report_id", "==", data.report_id),
        where("uid", "==", data.uid)
      );

      const userReportsSnapshot = await getDocs(userReportsQuery);
      const userReportRef = userReportsSnapshot.docs[0].ref;
      await updateDoc(userReportRef, {
        updated_at: (new Date().getTime() / 1000).toFixed(0),
      });

      return true;
    }

    return false;
  } catch (error) {
    console.error("Error updating user report comments:", error);
    return false;
  }
};

export const checkScreenshotOnUserReportAndMoveItToScreenshots = async () => {
  try {
    const userReportQuery = query(
      collection(db, "user_reports"),
      where("screenshot", "!=", null)
    );
    const userReportSnapshot = await getDocs(userReportQuery);

    for (const doc of userReportSnapshot.docs) {
      const userReport = doc.data();

      if (userReport.screenshot) {
        // If there is value on the screenshot field, move it to the screenshots array in the user report object
        const screenshot = userReport.screenshot;
        let screenshots = userReport.screenshots || [];

        // Only push the screenshot to the screenshots array if it's not already in the array
        if (!screenshots.includes(screenshot)) {
          screenshots.push(screenshot);
        }

        screenshots = Array.from(new Set(screenshots));

        // Update the user report with the new screenshots array
        await updateDoc(doc.ref, {
          screenshots,
        });
      }
    }

    return true;
  } catch (error) {
    console.error(
      "Error checking screenshot on user report and moving it to screenshots:",
      error
    );
    return false;
  }
};

export const normalizeUpdatedAtField = async () => {
  try {
    const userReportQuery = query(collection(db, "user_reports"));
    const userReportSnapshot = await getDocs(userReportQuery);

    const changedReportIds = [];

    for (const doc of userReportSnapshot.docs) {
      const userReport = doc.data();

      // Check if updated_at is a string
      if (typeof userReport.updated_at === "string") {
        // Convert the string to a timestamp and update the document
        const timestamp = parseInt(userReport.updated_at);

        await updateDoc(doc.ref, {
          updated_at: timestamp,
        });

        changedReportIds.push(doc.id);
      }
    }

    console.log(
      `Updated ${changedReportIds.length} user reports with string updated_at field to timestamp.`
    );

    return true;
  } catch (error) {
    console.error("Error normalizing updated_at fields:", error);
  }
};

export const getGames = async () => {
  try {
    const gamesCollection = collection(db, 'games');
    const gamesSnapshot = await getDocs(gamesCollection);
    const gamesList = gamesSnapshot.docs.map(doc => ({
      game_value: doc.id,
      game_name: doc.data().name || doc.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      ...doc.data()
    }));
    console.log("Games fetched from Firestore:", gamesList);
    return gamesList;
  } catch (error) {
    console.error("Error fetching games from Firestore:", error);
    throw error;
  }
};

export const setMessageSeen = async (
  messageId: string,
  uid: string,
  status: boolean
) => {
  try {
    if (uid === null) {
      console.error("User id is null or undefined");
      return;
    }

    // Check if a messages_seen record already exists for this message and user
    const { data: existingRecord, error: queryError } = await supabase
      .from('messages_seen')
      .select('*')
      .eq('message_id', messageId)
      .eq('user_id', uid)
      .maybeSingle();

    if (queryError) {
      throw queryError;
    }

    if (existingRecord) {
      // If a record exists, update it
      const { error: updateError } = await supabase
        .from('messages_seen')
        .update({
          seen: status,
          seen_at: status ? new Date().toISOString() : null,
        })
        .eq('message_id', messageId)
        .eq('user_id', uid);

      if (updateError) {
        throw updateError;
      }
    } else {
      // If no record exists, create a new one
      const { error: insertError } = await supabase
        .from('messages_seen')
        .insert({
          message_id: messageId,
          user_id: uid,
          seen: status,
          seen_at: status ? new Date().toISOString() : null,
        });

      if (insertError) {
        throw insertError;
      }
    }

    return true;
  } catch (error) {
    console.error("Error setting message seen:", error);
    throw error;
  }
};

export const fetchRewardsGroupedByGameNameOfUser = async (uid: string) => {
  try {
    const rewards = await fetchData("sys_rewards", {
      select: "*",
      filter: {
        uid: uid
      }
    });
    const rewardsGroupedByGameName = rewards.data.reduce(
      (acc: Record<string, number>, reward: any) => {
        const gameNameLowercased = reward.game_name.toLowerCase();
        if (!acc[gameNameLowercased]) {
          acc[gameNameLowercased] = 0;
        }
        acc[gameNameLowercased] += reward.mxs_gold_claimed;
        return acc;
      },
      {}
    );

    return rewardsGroupedByGameName;
  } catch (error) {
    console.error(
      "Error fetching rewards grouped by game name of user:",
      error
    );
  }
};

export const fetchLevelCompletedLeaderboard = async (user_id: any) => {
  try {
    const querySnapshot = await getDocs(
      query(
        collection(db, "level_complete_leaderboard"),
        where("uid", "==", user_id)
      )
    );

    if (querySnapshot.empty) {
      return null;
    } else {
      return querySnapshot.docs[0].data();
    }
  } catch (error) {
    console.error("Error fetching level completed leaderboard:", error);
    throw error;
  }
};

export const fetchMxsPointsLeaderboard = async (user_id: any) => {
  try {
    const querySnapshot = await getDocs(
      query(collection(db, "points_leaderboard"), where("uid", "==", user_id))
    );

    if (querySnapshot.empty) {
      return null;
    } else {
      return querySnapshot.docs[0].data();
    }
  } catch (error) {
    console.error("Error fetching mxs points leaderboard:", error);
    throw error;
  }
};

export const getGamesFromWebsiteCollection = async (osTypes: any[]) => {
  try {
    const gamesCollectionRef = collection(db, "website");
    const gamesQuery = query(gamesCollectionRef);
    const gamesQuerySnapshot = await getDocs(gamesQuery);
    const gamesData = gamesQuerySnapshot.docs.map((doc) => doc.data())[0]
      .games_page.games;

    let newGamesData: any[] = [];
    if (osTypes && osTypes.length > 0) {
      for (let i = 0; i < gamesData.length; i++) {
        const game = gamesData[i];

        if (!game._id.startsWith("drafts")) {
          for (let j = 0; j < game.operatingSystem.length; j++) {
            if (
              osTypes.includes(game.operatingSystem[j].name) &&
              !newGamesData.includes(game)
            ) {
              newGamesData.push(game);
            }
          }
        }
      }
    } else {
      newGamesData = gamesData;
    }

    newGamesData.sort((a, b) => {
      return b.playable - a.playable;
    });

    newGamesData.sort((a, b) => {
      return (
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
    });

    newGamesData.sort((a, b) => {
      return a.slug["current"] === "game-web-match"
        ? -1
        : b.slug["current"] === "game-web-match"
        ? 1
        : 0;
    });

    if (newGamesData.length > 0) {
      return newGamesData;
    } else {
      return gamesData;
    }
  } catch (error) {
    console.error("Error fetching games collection: ", error);
    return [];
  }
};
